'use client';

import Link from 'next/link';
import classNames from '@/utils/classNames';
import styles from './GlitchButton.module.css';

import type { ComponentPropsWithRef, ReactNode } from 'react';

type CommonProps = {
  withArrow?: boolean;
  children?: ReactNode;
  className?: string;
};

type LinkVariantProps = CommonProps &
  Omit<ComponentPropsWithRef<typeof Link>, keyof CommonProps>;

type ButtonVariantProps = CommonProps &
  Omit<ComponentPropsWithRef<'button'>, keyof CommonProps> & {
    href?: never;
  };

export type GlitchButtonProps = LinkVariantProps | ButtonVariantProps;

const ArrowIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.arrow}
  >
    <path
      d="M9.13086 5L4.13086 10L2.89258 8.76074L5.77734 5.87695H0V4.125H5.7793L2.89258 1.23926L4.13086 0L9.13086 5Z"
      fill="currentColor"
    />
  </svg>
);

// Shared brand-CTA classes (layout, type, focus ring). Flat link variants (e.g. the error-screen
// back links) reuse this and add their own background; GlitchButton's own background is its hover span.
export const brandButtonClassName =
  'inline-flex select-none items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-stone-50 no-underline outline-hidden focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-greyscale-950';

export default function GlitchButton({
  withArrow = true,
  className,
  children,
  ...props
}: GlitchButtonProps) {
  const content = (
    <>
      <span aria-hidden="true" className={styles.bg} />
      {children}
      {withArrow && <ArrowIcon />}
    </>
  );

  if ('href' in props && props.href !== undefined) {
    return (
      <Link
        className={classNames(styles.root, brandButtonClassName, className)}
        {...(props as LinkVariantProps)}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={classNames(styles.root, brandButtonClassName, className)}
      {...(props as ButtonVariantProps)}
    >
      {content}
    </button>
  );
}
